<template>
  <div class="mt-4 px-4 container-fluid">
    <div class="breadcrumb text-md px-0 mb-4">
      <p>
        <span class="breadcrumb-item" @click="irInicio">Inicio</span> > Panel Tipster
      </p>
    </div>
  </div>

  <div class="px-4 container-fluid">
    <div class="panel-sticky-header">
    <div class="row">
      <div class="col-12">
        <div class="card ficha-buscador">
          <div class="selector-row px-4 pt-3 pb-2">
            <div class="select-block" v-if="canAdmin">
              <label class="text-sm mb-2">Tipster</label>
              <div class="select-wrap">
                <select class="form-select" v-model="tipsterId">
                  <option value="">-- Yo (mi tipster) --</option>
                  <option v-for="t in tipsters" :key="t.id" :value="String(t.id)">
                    {{ t.nombre }}
                  </option>
                </select>
              </div>
              <div v-if="tipsters.length === 0" class="mt-3 alert alert-secondary text-sm">
                No hay tipsters en la lista (o no tienes permisos para verlos).
              </div>
            </div>

            <div class="selector-actions" v-if="canAdmin">
              <QBtn variant="tipster" label="Cargar" :loading="loading" :disabled="loading" @click="cargarPanel" />
            </div>
          </div>

          <!-- ── Filtro de período ── -->
          <div class="filtro-periodo px-4 pb-4">
            <div class="periodo-btns">
              <button
                v-for="p in periodos"
                :key="p.value"
                :class="['btn-periodo', { active: periodo === p.value }]"
                :disabled="loading"
                @click="cambiarPeriodo(p.value)"
              >
                <i v-if="p.value === 'custom'" class="fas fa-calendar-alt me-1"></i>
                {{ p.label }}
              </button>
            </div>

            <div v-if="periodo === 'custom'" class="custom-dates mt-2">
              <input type="date" v-model="fechaDesde" class="form-control date-input" :max="fechaHasta || undefined" />
              <span class="sep-dash">—</span>
              <input type="date" v-model="fechaHasta" class="form-control date-input" :min="fechaDesde || undefined" />
              <QBtn
                variant="tipster"
                label="Aplicar"
                :loading="loading"
                :disabled="!fechaDesde || !fechaHasta || loading"
                @click="cargarPanel"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Barra de navegación de secciones ── -->
    <div class="secnav-bar">
      <button v-for="s in seccionesNav" :key="s.id"
        :class="['secnav-btn', s.primary ? 'secnav-btn--primary' : '']"
        :style="{ '--sc': s.color }"
        @click="irSeccion(s.id)">
        <i :class="s.icon"></i>{{ s.label }}
      </button>
    </div>
    </div><!-- /panel-sticky-header -->

    <!-- 1-11: métricas generales -->
    <div id="sec-kpis" class="sec-anchor" style="--sc:#84cc16">
      <PanelTipsterKpis
        :kpis="kpis"
        @open-ventas-detalle="modalVentasVisible = true"
        @open-ingresos-detalle="modalIngresosVisible = true"
        @open-renovaciones-detalle="modalRenovacionesVisible = true"
        @open-top-ventas-detalle="modalTopVentasVisible = true"
        @open-top-ingresos-detalle="modalTopIngresosVisible = true"
        @open-devoluciones-detalle="modalDevolucionesVisible = true"
      />
      <PanelTipsterEvolucion :evolucion="evolucion" :fecha-desde="fechaDesde" />
    </div>

    <div id="sec-actividad" class="sec-anchor" style="--sc:#06b6d4">
      <PanelTipsterHeatmap :heatmap="heatmap" />
    </div>

    <div id="sec-paises" class="sec-anchor" style="--sc:#10b981">
      <PanelTipsterPaises :paises-ventas="paisesTodos" :paises-importe="paisesTodos" :kpis="kpis" />
    </div>

    <div id="sec-productos" class="sec-anchor" style="--sc:#f59e0b">
      <PanelTipsterProductos :productos="productos" />
    </div>

    <div id="sec-descuentos" class="sec-anchor" style="--sc:#f97316">
      <PanelTipsterDescuentos :descuentos="descuentos" />
      <PanelTipsterEfectividadDesc :datos="efectividadDesc" />
    </div>

    <div id="sec-pedidos" class="sec-anchor" style="--sc:#6366f1">
      <PanelTipsterPedidos
        :pedidos="pedidosLista"
        :loading="loading"
        :can-admin="canAdmin"
        :tipster-id="tipsterIdEfectivo"
        :date-from="dateRange.date_from"
        :date-to="dateRange.date_to"
      />
      <PanelTipsterDevoluciones :devoluciones="devoluciones" />
      <PanelTipsterTipoCompra :datos="tipoCompra" />
    </div>

    <div id="sec-metodos" class="sec-anchor" style="--sc:#a855f7">
      <PanelTipsterMetodosPago :datos="metodosPago" />
      <PanelTipsterEmbudo :embudo="embudo" />
    </div>

    <!-- Separador suscripción -->
    <div class="seccion-separador mt-5">
      <span class="seccion-separador__label">Suscripción</span>
    </div>

    <div id="sec-suscripciones" class="sec-anchor" style="--sc:#d4af37">
      <PanelTipsterSuscripciones :datos="suscripciones" :tipster-id="tipsterIdEfectivo" />
      <PanelTipsterSuscriptores :evolucion="evolucionSuscriptores" />
      <PanelTipsterRetencion :retencion="retencion" />
    </div>

    <!-- Sección campañas: solo visible si el tipster tiene panel_campanas activo -->
    <template v-if="panelCampanas">
      <div class="seccion-separador mt-5">
        <span class="seccion-separador__label">Campañas</span>
      </div>

      <PanelTipsterResumen
        :resumen="resumen"
        :tipster-label="tipsterLabel"
        :loading="loading"
        :vacio="!loading && campanas.length === 0 && productos.length === 0 && descuentos.length === 0"
        :num-pasarelas="numPasarelas"
      />
      <PanelTipsterConversionCampana :campanas="conversionCampana" />
      <PanelTipsterCampanas
        :campanas="campanas"
        :resumen="resumen"
        v-model:mostrarBorrados="mostrarBorrados"
        @update:mostrarBorrados="onMostrarBorradosChange"
      />
    </template>
  </div>

  <ModalDetalleVentas
    :visible="modalVentasVisible"
    :date-from="dateRange.date_from"
    :date-to="dateRange.date_to"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    @close="modalVentasVisible = false"
  />

  <ModalResumenIngresos
    :visible="modalIngresosVisible"
    :date-from="dateRange.date_from"
    :date-to="dateRange.date_to"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    @close="modalIngresosVisible = false"
  />

  <ModalRenovacionesActivas
    :visible="modalRenovacionesVisible"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    @close="modalRenovacionesVisible = false"
  />

<ModalDetalleVentas
    :visible="modalTopVentasVisible"
    :titulo="kpis?.producto_top_ventas?.producto ? 'Compradores — ' + kpis.producto_top_ventas.producto : 'Compradores'"
    :date-from="dateRange.date_from"
    :date-to="dateRange.date_to"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    :producto-id="kpis?.producto_top_ventas?.producto_id || null"
    @close="modalTopVentasVisible = false"
  />

  <ModalDetalleVentas
    :visible="modalTopIngresosVisible"
    :titulo="kpis?.producto_top_ingresos?.producto ? 'Compradores — ' + kpis.producto_top_ingresos.producto : 'Compradores'"
    :date-from="dateRange.date_from"
    :date-to="dateRange.date_to"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    :producto-id="kpis?.producto_top_ingresos?.producto_id || null"
    @close="modalTopIngresosVisible = false"
  />

  <ModalDetalleDevoluciones
    :visible="modalDevolucionesVisible"
    :date-from="dateRange.date_from"
    :date-to="dateRange.date_to"
    :tipster-id="tipsterIdEfectivo || null"
    :can-admin="canAdmin"
    @close="modalDevolucionesVisible = false"
  />
</template>

<script>
import showSwal from "@/mixins/showSwal.js";
import PanelTipsterResumen   from "./components/panel-tipster/PanelTipsterResumen.vue";
import PanelTipsterKpis      from "./components/panel-tipster/PanelTipsterKpis.vue";
import PanelTipsterPaises    from "./components/panel-tipster/PanelTipsterPaises.vue";
import PanelTipsterProductos from "./components/panel-tipster/PanelTipsterProductos.vue";
import PanelTipsterDescuentos from "./components/panel-tipster/PanelTipsterDescuentos.vue";
import PanelTipsterCampanas  from "./components/panel-tipster/PanelTipsterCampanas.vue";
import PanelTipsterEvolucion from "./components/panel-tipster/PanelTipsterEvolucion.vue";
import PanelTipsterHeatmap        from "./components/panel-tipster/PanelTipsterHeatmap.vue";
import PanelTipsterSuscriptores   from "./components/panel-tipster/PanelTipsterSuscriptores.vue";
import PanelTipsterEmbudo         from "./components/panel-tipster/PanelTipsterEmbudo.vue";
import PanelTipsterDevoluciones   from "./components/panel-tipster/PanelTipsterDevoluciones.vue";
import PanelTipsterTipoCompra     from "./components/panel-tipster/PanelTipsterTipoCompra.vue";
import PanelTipsterEfectividadDesc from "./components/panel-tipster/PanelTipsterEfectividadDesc.vue";
import PanelTipsterRetencion          from "./components/panel-tipster/PanelTipsterRetencion.vue";
import PanelTipsterConversionCampana  from "./components/panel-tipster/PanelTipsterConversionCampana.vue";
import PanelTipsterSuscripciones      from "./components/panel-tipster/PanelTipsterSuscripciones.vue";
import PanelTipsterPedidos            from "./components/panel-tipster/PanelTipsterPedidos.vue";
import PanelTipsterMetodosPago        from "./components/panel-tipster/PanelTipsterMetodosPago.vue";
import ModalDetalleVentas             from "./components/panel-tipster/ModalDetalleVentas.vue";
import ModalResumenIngresos           from "./components/panel-tipster/ModalResumenIngresos.vue";
import ModalRenovacionesActivas       from "./components/panel-tipster/ModalRenovacionesActivas.vue";
import ModalDetalleDevoluciones       from "./components/panel-tipster/ModalDetalleDevoluciones.vue";

export default {
  name: "PanelTipster",
  components: {
    PanelTipsterResumen,
    PanelTipsterKpis,
    PanelTipsterPaises,
    PanelTipsterProductos,
    PanelTipsterDescuentos,
    PanelTipsterCampanas,
    PanelTipsterEvolucion,
    PanelTipsterHeatmap,
    PanelTipsterSuscriptores,
    PanelTipsterEmbudo,
    PanelTipsterDevoluciones,
    PanelTipsterTipoCompra,
    PanelTipsterEfectividadDesc,
    PanelTipsterRetencion,
    PanelTipsterSuscripciones,
    PanelTipsterConversionCampana,
    PanelTipsterPedidos,
    PanelTipsterMetodosPago,
    ModalDetalleVentas,
    ModalResumenIngresos,
    ModalRenovacionesActivas,
    ModalDetalleDevoluciones,
  },
  data() {
    return {
      loading: false,
      modalVentasVisible: false,
      modalIngresosVisible: false,
      modalRenovacionesVisible: false,
      modalTopVentasVisible: false,
      modalTopIngresosVisible: false,
      modalDevolucionesVisible: false,
      tipsters: [],
      tipsterId: "",
      mostrarBorrados: false,
      periodo: 'mes',
      fechaDesde: '',
      fechaHasta: '',
      periodos: [
        { value: 'mes',      label: 'Mes actual' },
        { value: 'hoy',      label: 'Hoy' },
        { value: 'hoy_ayer', label: 'Hoy y Ayer' },
        { value: 'anio',     label: 'Este año' },
        { value: 'all',      label: 'Todo' },
        { value: 'custom',   label: 'Personalizado' },
      ],
      seccionesNav: [
        { id: 'sec-kpis',         label: 'KPIs',              icon: 'fas fa-chart-bar me-1',    color: '#84cc16', primary: true },
        { id: 'sec-pedidos',      label: 'Pedidos',           icon: 'fas fa-receipt me-1',      color: '#6366f1', primary: true },
        { id: 'sec-suscripciones',label: 'Suscripciones',     icon: 'fas fa-repeat me-1',       color: '#d4af37', primary: true },
        { id: 'sec-actividad',    label: 'Actividad horaria', icon: 'fas fa-fire me-1',         color: '#06b6d4' },
        { id: 'sec-paises',       label: 'Top países',        icon: 'fas fa-globe me-1',        color: '#10b981' },
        { id: 'sec-productos',    label: 'Productos',         icon: 'fas fa-box me-1',          color: '#f59e0b' },
        { id: 'sec-descuentos',   label: 'Descuentos',        icon: 'fas fa-tag me-1',          color: '#f97316' },
        { id: 'sec-metodos',      label: 'Métodos de pago',   icon: 'fas fa-credit-card me-1',  color: '#a855f7' },
      ],
      panelCampanas: false,
      campanas: [],
      resumen: {
        campanas_total: 0,
        campanas_activas: 0,
        entradas_total: 0,
        entradas_unicas_total: 0,
        suscritos_actuales_total: 0,
        salidas_total: 0,
        reingresos_total: 0,
        compras_total: 0,
        importe_total: 0,
        importe_pendiente: 0,
        trials_pendientes: 0,
      },
      paisesVentas: [],
      paisesImporte: [],
      paisesTodos: [],
      kpis: {
        ventas_totales: 0,
        ingresos_totales: 0,
        ticket_medio: 0,
        trials_pendientes: 0,
        importe_pendiente: 0,
        producto_top_ventas: null,
        producto_top_ingresos: null,
      },
      productos: [],
      descuentos: [],
      numPasarelas: 0,
      pedidosLista: [],
      evolucion: [],
      heatmap: [],
      evolucionSuscriptores: [],
      embudo: { entradas: 0, suscritos: 0, compradores: 0, recurrentes: 0 },
      devoluciones: { total: 0, importe: 0, tasa_unidades: 0, tasa_importe: 0, por_motivo: [], por_mes: [] },
      tipoCompra: { anonimas: 0, registrados: 0, importe_anonimas: 0, importe_registrados: 0, ticket_anonimas: 0, ticket_registrados: 0, total: 0, importe_total: 0 },
      efectividadDesc: { ventas_con_cupon: 0, ventas_sin_cupon: 0, importe_con_cupon: 0, importe_sin_cupon: 0, tasa_uso: 0, ticket_con_cupon: 0, ticket_sin_cupon: 0, por_codigo: [] },
      retencion: { global: null, cohortes: [] },
      conversionCampana: [],
      suscripciones: { activas: 0, en_gracia: 0, vencidas: 0, canceladas: 0, por_vencer_7d: 0, lista: [] },
      metodosPago: [],
    };
  },
  computed: {
    permisos() {
      return this.$store.state.auth?.permisos_finales || [];
    },
    canAdmin() {
      return Array.isArray(this.permisos) && this.permisos.includes("panel_tipster.admin.ver");
    },
    tipsterLabel() {
      const attrs = this.$store.state.auth?.me?.data?.attributes || {};
      if (!this.canAdmin || !this.tipsterId) {
        const t = attrs.tipster;
        return t?.nombre || attrs.name || attrs.username || "Mi Tipster";
      }
      const t = this.tipsters.find(x => String(x.id) === String(this.tipsterId));
      return t ? t.nombre : `ID ${this.tipsterId}`;
    },
    tipsterIdEfectivo() {
      if (this.canAdmin) return this.resolveTipsterIdAdmin();
      const attrs = this.$store.state.auth?.me?.data?.attributes || {};
      return attrs?.tipster?.id ?? null;
    },
    dateRange() {
      if (this.periodo === 'all') return { date_from: null, date_to: null };
      if (this.periodo === 'custom') {
        return { date_from: this.fechaDesde || null, date_to: this.fechaHasta || null };
      }
      const hoy = new Date();
      const fmt = d => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      if (this.periodo === 'hoy') {
        return { date_from: fmt(hoy), date_to: fmt(hoy) };
      }
      if (this.periodo === 'hoy_ayer') {
        const ayer = new Date(hoy);
        ayer.setDate(ayer.getDate() - 1);
        return { date_from: fmt(ayer), date_to: fmt(hoy) };
      }
      if (this.periodo === 'anio') {
        const inicioAnio = new Date(hoy.getFullYear(), 0, 1);
        return { date_from: fmt(inicioAnio), date_to: fmt(hoy) };
      }
      if (this.periodo === 'mes') {
        const inicioMesActual = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        return { date_from: fmt(inicioMesActual), date_to: fmt(hoy) };
      }
      const meses = parseInt(this.periodo);
      const desde = new Date(hoy);
      desde.setMonth(desde.getMonth() - meses);
      return { date_from: fmt(desde), date_to: fmt(hoy) };
    },
    periodoLabel() {
      if (this.periodo === 'all') return 'Todo';
      if (this.periodo === 'custom' && this.fechaDesde && this.fechaHasta)
        return `${this.fechaDesde} → ${this.fechaHasta}`;
      const p = this.periodos.find(x => x.value === this.periodo);
      return p ? p.label : '';
    },
  },
  watch: {
    canAdmin: {
      immediate: true,
      handler(n) {
        if (n) this.cargarTipstersAdmin();
      }
    }
  },
  methods: {
    irInicio() {
      this.$router.push("/dashboard");
    },
    irSeccion(id) {
      const el = document.getElementById(id);
      if (!el) return;
      const header = document.querySelector('.panel-sticky-header');
      const offset = header ? header.offsetHeight + 8 : 16;
      // Detecta el contenedor real: busca el primer elemento que tiene overflow activo o ya scrolleó
      const candidates = [
        document.querySelector('.main-content'),
        document.body,
        document.documentElement,
      ];
      const scroller = candidates.find(c => {
        if (!c) return false;
        const oy = getComputedStyle(c).overflowY;
        return (oy === 'auto' || oy === 'scroll') && c.scrollHeight > c.clientHeight + 2;
      }) || candidates.find(c => c && c.scrollTop > 0) || document.body;
      const elRect = el.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const target = scroller.scrollTop + (elRect.top - scrollerRect.top) - offset;
      scroller.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    },
    resolveTipsterIdAdmin() {
      const attrs = this.$store.state.auth?.me?.data?.attributes || {};
      if (this.tipsterId) return Number(this.tipsterId);
      if (attrs?.tipster?.id) return Number(attrs.tipster.id);
      if (this.tipsters?.length) return Number(this.tipsters[0].id);
      return null;
    },
    async cargarTipstersAdmin() {
      try {
        const resp = await this.$store.dispatch("auth/listaTipsters", {});
        const arr = Array.isArray(resp) ? resp : (resp?.data || []);
        this.tipsters = arr || [];
      } catch (e) {
        console.error(e);
      }
    },
    async onMostrarBorradosChange(val) {
      this.mostrarBorrados = val;
      await this.cargarPanel();
    },
    async cambiarPeriodo(p) {
      this.periodo = p;
      if (p !== 'custom') await this.cargarPanel();
    },
    async cargarPanel() {
      this.loading = true;
      try {
        const { date_from, date_to } = this.dateRange;
        const payload = {
          mostrarBorrados: this.mostrarBorrados ? 1 : 0,
          mostrar_borrados: this.mostrarBorrados ? 1 : 0,
          ...(date_from ? { date_from } : {}),
          ...(date_to   ? { date_to   } : {}),
        };

        let resp;
        if (this.canAdmin) {
          const tid = this.resolveTipsterIdAdmin();
          if (!tid) await this.cargarTipstersAdmin();
          const tid2 = this.resolveTipsterIdAdmin();
          if (!tid2) {
            this.campanas = [];
            this.productos = [];
            this.descuentos = [];
            this.paisesVentas = { labels: [], data: [] };
            this.paisesImporte = { labels: [], data: [] };
            this.resumen = { ...this.resumen, campanas_total: 0 };
            return;
          }
          if (!this.tipsterId) this.tipsterId = String(tid2);
          resp = await this.$store.dispatch("auth/tipsterPanelAdmin", { tipsterId: tid2, payload });
        } else {
          resp = await this.$store.dispatch("auth/tipsterPanel", payload);
        }

        const data = resp?.data?.attributes ?? resp?.data ?? resp?.attributes ?? resp;

        this.panelCampanas = data?.panel_campanas ?? false;
        this.campanas  = data?.campanas  || [];
        this.resumen   = data?.resumen   || this.resumen;
        this.kpis      = data?.kpis      || this.kpis;
        this.productos = data?.productos || [];
        this.descuentos = data?.descuentos || [];
        this.numPasarelas = data?.num_pasarelas ?? 0;
        this.evolucion    = Array.isArray(data?.evolucion_mensual) ? data.evolucion_mensual : [];
        this.heatmap               = Array.isArray(data?.heatmap) ? data.heatmap : [];
        this.evolucionSuscriptores = Array.isArray(data?.evolucion_suscriptores) ? data.evolucion_suscriptores : [];
        this.embudo                = data?.embudo ?? this.embudo;
        this.devoluciones          = data?.devoluciones ?? this.devoluciones;
        this.tipoCompra            = data?.anonimos_vs_registrados ?? this.tipoCompra;
        this.efectividadDesc       = data?.efectividad_descuentos  ?? this.efectividadDesc;
        this.retencion             = data?.retencion               ?? this.retencion;
        this.conversionCampana     = Array.isArray(data?.conversion_por_campana) ? data.conversion_por_campana : [];
        this.suscripciones         = data?.suscripciones ?? this.suscripciones;
        this.pedidosLista          = Array.isArray(data?.pedidos_lista) ? data.pedidos_lista : [];

        this.paisesVentas  = Array.isArray(data?.paises_top_ventas)  ? data.paises_top_ventas  : [];
        this.paisesImporte = Array.isArray(data?.paises_top_importe) ? data.paises_top_importe : [];
        this.paisesTodos   = Array.isArray(data?.paises_todos)       ? data.paises_todos       : [];
        this.metodosPago   = Array.isArray(data?.metodos_pago) ? data.metodos_pago : [];
      } catch (e) {
        showSwal.methods.showSwal({
          type: "error",
          message: "No se pudo cargar el panel del tipster",
          width: 520,
        });
        this.campanas  = [];
        this.productos = [];
        this.descuentos = [];
        this.evolucion  = [];
        this.heatmap               = [];
        this.evolucionSuscriptores = [];
        this.embudo                = { entradas: 0, suscritos: 0, compradores: 0, recurrentes: 0 };
        this.devoluciones          = { total: 0, importe: 0, tasa_unidades: 0, tasa_importe: 0, por_motivo: [], por_mes: [] };
        this.tipoCompra            = { anonimas: 0, registrados: 0, importe_anonimas: 0, importe_registrados: 0, ticket_anonimas: 0, ticket_registrados: 0, total: 0, importe_total: 0 };
        this.efectividadDesc       = { ventas_con_cupon: 0, ventas_sin_cupon: 0, importe_con_cupon: 0, importe_sin_cupon: 0, tasa_uso: 0, ticket_con_cupon: 0, ticket_sin_cupon: 0, por_codigo: [] };
        this.retencion             = { global: null, cohortes: [] };
        this.conversionCampana     = [];
        this.pedidosLista          = [];
        this.paisesVentas  = [];
        this.paisesImporte = [];
        this.paisesTodos   = [];
        this.kpis = {
          ventas_totales: 0, ingresos_totales: 0, ticket_medio: 0,
          trials_pendientes: 0, importe_pendiente: 0,
          producto_top_ventas: null, producto_top_ingresos: null,
        };
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.$store.commit('setShowFooter', true);
    if (this.canAdmin) {
      await this.cargarTipstersAdmin();
    }
    await this.cargarPanel();
  },
};
</script>

<style scoped>
.container-fluid { max-width: 1100px; }

/* ── Header sticky (filtros + nav) ── */
.panel-sticky-header {
  position: sticky;
  top: 0;
  z-index: 200;
  background: var(--q-bg, #070b12);
  padding-bottom: 4px;
  margin-bottom: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,.5);
}
@media (max-width: 768px) {
  .panel-sticky-header { box-shadow: 0 2px 8px rgba(0,0,0,.4); }
}

/* ── Nav de secciones ── */
.secnav-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 12px 0 4px;
}
.secnav-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border-radius: 20px;
  border: 1.5px solid var(--sc);
  color: var(--sc);
  background: transparent;
  font-size: .78rem;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, opacity .15s;
  white-space: nowrap;
}
.secnav-btn:hover {
  background: color-mix(in srgb, var(--sc) 12%, transparent);
}
.secnav-btn--primary {
  background: color-mix(in srgb, var(--sc) 16%, transparent);
  border-width: 2px;
  font-weight: 700;
  letter-spacing: .01em;
}
.secnav-btn--primary:hover {
  background: color-mix(in srgb, var(--sc) 28%, transparent);
}

/* ── Borde lateral coloreado por sección ── */
.sec-anchor {
  border-left: 3px solid var(--sc);
  padding-left: 6px;
  margin-left: -9px;
}
@media (max-width: 768px) {
  /* Card de filtros — quitar overflow para que el scroll interno funcione */
  .ficha-buscador { overflow: visible !important; }
  .ficha-buscador .selector-row { padding: 10px 12px 6px !important; gap: 8px; }
  .ficha-buscador .filtro-periodo {
    padding: 10px 0 10px !important;
    overflow: visible !important;
  }

  /* Selector tipster + Cargar en misma fila, más compactos */
  .select-block { flex: 1 1 auto; }
  .select-block label { display: none; }
  .select-wrap { min-width: 0; }
  .selector-actions { flex-wrap: nowrap; }

  /* Botones de período: scroll horizontal, una sola fila */
  .periodo-btns {
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding: 0 12px 4px;
    margin: 0;
    width: 100%;
  }
  .periodo-btns::-webkit-scrollbar { display: none; }
  .btn-periodo { white-space: nowrap !important; flex-shrink: 0 !important; padding: 4px 10px; font-size: .76rem; }

  /* Nav pills: scroll horizontal, una sola fila */
  .secnav-bar {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    justify-content: flex-start;
    padding: 8px 0 4px;
  }
  .secnav-bar::-webkit-scrollbar { display: none; }
  .secnav-btn { flex-shrink: 0; font-size: .72rem; padding: 4px 10px; }

  .sec-anchor { margin-left: -4px; padding-left: 4px; }
}

/* El ficha-buscador en panel usa layout de bloque, no flex */
.ficha-buscador { display: block !important; }

.selector-row { display: flex; gap: 12px; align-items: end; flex-wrap: wrap; }

.select-wrap {
  position: relative;
  min-width: min(320px, 100%);
  max-width: 520px;
  flex: 1 1 auto;
}
.select-wrap select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
}
.select-wrap .form-select {
  --bs-form-select-bg-img: none !important;
  background-image: none !important;
  padding-right: 46px;
}
.select-wrap::after {
  content: "";
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-25%);
  width: 0; height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid rgba(0,0,0,.45);
  pointer-events: none;
}

.selector-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; }

.toggle-block { min-width: min(320px, 100%); padding-bottom: 2px; }
.toggle-line { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 42px; }
.toggle-switch { display: flex; align-items: center; white-space: nowrap; }
.toggle-switch .form-check-input { margin-top: 0; }

/* ── Filtro de período ── */
.filtro-periodo {
  border-top: 1px solid var(--q-border);
  padding-top: 14px !important;
}
.periodo-btns { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-periodo {
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid var(--q-border-md);
  background: transparent;
  color: var(--q-muted);
  font-size: .8rem;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
  white-space: nowrap;
}
.btn-periodo:hover:not(:disabled) { border-color: var(--q-gold); color: var(--q-text); }
.btn-periodo.active {
  background: var(--q-gold);
  border-color: var(--q-gold);
  color: #140c00;
  font-weight: 600;
}
.btn-periodo:disabled { opacity: .5; cursor: not-allowed; }

.custom-dates {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.date-input {
  max-width: 150px;
  font-size: .85rem;
  background: var(--q-card);
  color: var(--q-text);
  border-color: var(--q-border-md);
}
.date-input:focus { border-color: var(--q-gold); box-shadow: none; }
.sep-dash { color: var(--q-muted); font-size: .9rem; }

@media (max-width: 575px) {
  .selector-actions { margin-left: 0; width: 100%; }
  .selector-actions .btn { width: 100%; }
  .toggle-block { width: 100%; min-width: 0; }
  .btn-periodo { font-size: .75rem; padding: 4px 10px; }
  .date-input { max-width: calc(50vw - 40px); }
}

.seccion-separador {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}
.seccion-separador::before,
.seccion-separador::after {
  content: '';
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--q-gold, #f5a623), transparent);
}
.seccion-separador__label {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--q-gold, #f5a623);
  padding: 4px 14px;
  border: 1px solid var(--q-gold, #f5a623);
  border-radius: 999px;
  opacity: 0.85;
}
</style>
